/**
 * Resolução dinâmica em malha fechada (PERFORMANCE M2).
 *
 * É a alavanca de maior retorno em mobile, e a diferença entre amador e
 * profissional aqui é ter um CONTROLADOR, não um menu de qualidade.
 *
 * Histerese e cooldown existem para o jogador NEVER ver a imagem "respirando":
 * ajuste em passo pequeno, com intervalo mínimo entre mudanças.
 *
 * A decisão é sobre a MEDIANA de uma janela, e NEVER sobre o último frame.
 * MEDIDO em `T-DMA`, celular 428x926: os picos de frame moram nos primeiros
 * 20 s (119, 173 e 166 ms), e depois dos 25 s o aparelho anda entre 19 e 40 ms.
 * Lendo um frame por vez, UM pico de carga bastava para a imagem descer um
 * degrau — o jogo inteiro rebaixado por causa de coisa que só acontece na
 * abertura. O cooldown NEVER cobria isso: ele limita a FREQUÊNCIA do ajuste, e
 * NEVER o ruído da amostra.
 *
 * Mediana, e NEVER média: é a estatística que ignora o outlier por construção,
 * pelo mesmo motivo que `FrameSampler` mede p95 e pior 1% em vez de média.
 */
import { tierSpec, type TierKey } from '@game/gamedata/perf';

export interface RenderScaleOptions {
  min: number;
  max: number;
  /** Orçamento de frame em ms (1000 / targetFps). */
  targetFrameMs: number;
  /** Passo por ajuste. Pequeno de propósito. */
  step: number;
  /** Zona morta em ms em volta do alvo, onde NEVER se ajusta. */
  hysteresisMs: number;
  /** Frames mínimos entre dois ajustes. */
  cooldownFrames: number;
  /**
   * Sobre quantos frames a mediana é tirada. Curta demais, um pico volta a
   * mandar; longa demais, o controlador demora a reagir a carga de verdade.
   */
  windowFrames: number;
}

/**
 * Amostras mínimas para QUALQUER decisão. Abaixo disto a janela ainda NEVER é
 * uma janela, e decidir seria adivinhar com cara de estatística.
 */
const MIN_SAMPLES = 8;

export function optionsForTier(key: TierKey): RenderScaleOptions {
  const spec = tierSpec(key);
  return {
    min: spec.renderScale.min,
    max: spec.renderScale.max,
    targetFrameMs: 1000 / spec.targetFps,
    step: 0.05,
    hysteresisMs: 1.5,
    cooldownFrames: 30,
    windowFrames: 30,
  };
}

export class RenderScaleController {
  private current: number;
  private framesSinceChange: number;
  /** Buffer circular dos frames recentes. Pré-alocado: `update` é caminho quente. */
  private readonly window: Float64Array;
  private readonly scratch: Float64Array;
  private writeIndex = 0;
  private filled = 0;

  constructor(private readonly options: RenderScaleOptions) {
    if (options.min <= 0 || options.min > options.max) {
      throw new Error('faixa de renderScale inválida');
    }
    if (!Number.isInteger(options.windowFrames) || options.windowFrames < MIN_SAMPLES) {
      throw new Error(`janela mínima é ${String(MIN_SAMPLES)} frames`);
    }
    this.window = new Float64Array(options.windowFrames);
    this.scratch = new Float64Array(options.windowFrames);
    this.current = options.max;
    // Começa em 0, NEVER em cooldownFrames: reagir ao primeiro frame é ruído,
    // não sinal. O controlador só age depois de uma janela inteira.
    this.framesSinceChange = 0;
  }

  get scale(): number {
    return this.current;
  }

  /** Volta para o topo da faixa. Usado ao trocar de tier. */
  reset(): void {
    this.current = this.options.max;
    this.framesSinceChange = 0;
    this.writeIndex = 0;
    this.filled = 0;
  }

  /**
   * Mediana da janela, sem alocar: `subarray` compartilha o buffer, e
   * `TypedArray.sort` é numérico e no lugar. Roda no MÁXIMO uma vez por
   * cooldown, e NEVER por frame.
   */
  private median(): number {
    const n = this.filled;
    for (let i = 0; i < n; i++) this.scratch[i] = this.window[i] ?? 0;
    this.scratch.subarray(0, n).sort();
    const middle = n >> 1;
    if ((n & 1) === 1) return this.scratch[middle] ?? 0;
    return ((this.scratch[middle - 1] ?? 0) + (this.scratch[middle] ?? 0)) / 2;
  }

  /**
   * Caminho quente: uma vez por frame. NEVER aloque aqui.
   * Devolve a escala a usar neste frame.
   */
  update(frameMs: number): number {
    if (!Number.isFinite(frameMs) || frameMs <= 0) return this.current;

    this.window[this.writeIndex] = frameMs;
    this.writeIndex = (this.writeIndex + 1) % this.options.windowFrames;
    if (this.filled < this.options.windowFrames) this.filled += 1;

    this.framesSinceChange += 1;
    if (this.framesSinceChange < this.options.cooldownFrames) return this.current;
    // Janela curta demais NEVER decide nada: na abertura o controlador prefere
    // esperar a rebaixar a imagem com base em três frames.
    if (this.filled < MIN_SAMPLES) return this.current;

    const { targetFrameMs, hysteresisMs, step, min, max } = this.options;
    const typical = this.median();
    const over = typical > targetFrameMs + hysteresisMs;
    const under = typical < targetFrameMs - hysteresisMs;

    if (over && this.current > min) {
      this.current = Math.max(min, roundScale(this.current - step));
      this.framesSinceChange = 0;
    } else if (under && this.current < max) {
      this.current = Math.min(max, roundScale(this.current + step));
      this.framesSinceChange = 0;
    }

    return this.current;
  }
}

/** Evita deriva de float acumulando passos de 0.05. */
function roundScale(value: number): number {
  return Math.round(value * 1000) / 1000;
}
